/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { HorizontalSymbolTextLogo } from "components/atoms/HorizontalSymbolTextLogo/HorizontalSymbolTextLogo";
import {
  FadeInUnderLineList,
  FadeInUnderLineListType,
} from "components/molecules/FadeInUnderLineList/FadeInUnderLineList";
import { SNSTextIconsType } from "components/molecules/SNSTextIcons/SNSTextIcons";
import React from "react";
import { mixin } from "styles/mixin";
import { defaultTheme } from "styles/theme";
import { constants } from "utils/constants";
import { ToLink } from "utils/ToLink";
import { BaseInner } from "utils/Wrapper/BaseInner/BaseInner";
import { NavFooter } from "../NavFooter/NavFooter";
import { Overlay } from "../Overlay/Overlay";

type Props = {
  show: boolean;
  slotProps: {
    logo: {
      onLogoClick?: React.MouseEventHandler<HTMLButtonElement>;
    };
    overlay: {
      onOverlayClick?: React.MouseEventHandler<HTMLButtonElement>;
    };
    navItems: {
      items: FadeInUnderLineListType[];
      onNavItemClick?: React.MouseEventHandler<HTMLButtonElement>;
      delayTime?: number;
      fadeInTime?: number;
    };
    footer: {
      iconProps: SNSTextIconsType;
      uiLibraryLink?: string;
    };
  };
};

export const NavMenu: React.FC<Props> = ({ show, slotProps }: Props) => {
  const { onLogoClick } = slotProps.logo;
  const { onOverlayClick } = slotProps.overlay;
  const { items, onNavItemClick } = slotProps.navItems;
  const { iconProps, uiLibraryLink = "" } = slotProps.footer;

  return (
    <Overlay
      cssOverride={css`
        z-index: ${defaultTheme.zIndex.navMenuOverlay};
      `}
      show={show}
      duration={0.5}
      onClick={onOverlayClick}
      color={defaultTheme.palette.common.overlay}
    >
      <BaseInner
        centered={false}
        width="100%"
        maxWidth="100%"
        cssOverride={css`
          position: fixed;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          overflow-y: ${show ? "auto" : "hidden"};
          overflow-x: ${show ? "hidden" : "auto"};

          padding-top: calc(
            ${defaultTheme.spacing.header.top} + 0.24rem
          ); // MEMO-9xkjadlkadfs: 縦方向の中央揃えのため調整
          color: ${defaultTheme.palette.common.white};

          @media screen and (min-width: ${defaultTheme.breakpoints.md}) {
            margin: 0 auto;
            padding-left: 0;
          }
        `}
      >
        {/* ロゴ */}
        <div
          css={css`
            display: flex;

            justify-content: flex-start;
            margin-bottom: ${defaultTheme.spacing.md};
            position: relative; // MEMO-9gusadlkwffs: 視覚上の左揃えのため調整
            left: -0.29rem; // MEMO-9gusadlkwffs: 視覚上の左揃えのため調整
            @media screen and (min-width: ${defaultTheme.breakpoints.md}) {
              justify-content: center;
              margin-bottom: ${defaultTheme.spacing.lg};
              position: static; // MEMO-9gusadlkwffs: 視覚上の左揃えのため調整
            }
          `}
        >
          <button
            onClick={onLogoClick}
            css={css`
              width: fit-content;
            `}
          >
            <ToLink
              pathname={constants.pages.home.url}
              scrollToID={constants.pages.home.scrollToID}
            >
              <HorizontalSymbolTextLogo dark />
            </ToLink>
          </button>
        </div>
        <div
          css={css`
            padding: 0 ${defaultTheme.spacing.xs};
            @media screen and (min-width: ${defaultTheme.breakpoints.md}) {
              padding: 0;
            }
          `}
        >
          {/* ナビリスト */}
          <div
            css={css`
              display: flex;
              flex-direction: column;

              align-items: flex-start;
              margin-bottom: ${defaultTheme.spacing.md};
              @media screen and (min-width: ${defaultTheme.breakpoints.md}) {
                align-items: center;
                margin-bottom: ${defaultTheme.spacing.lg};
              }
            `}
          >
            <FadeInUnderLineList
              items={items}
              onClick={onNavItemClick}
              delayTime={0.125}
              fadeInTime={2}
              slotProps={{
                container: {
                  style: css`
                    row-gap: ${defaultTheme.spacing.xxs};
                    @media screen and (min-width: ${defaultTheme.breakpoints
                        .md}) {
                      row-gap: ${defaultTheme.spacing.xs};
                    }
                  `,
                },
                item: {
                  text: {
                    style: css`
                      ${mixin.text.navMenuItem};
                    `,
                  },
                },
              }}
            />
          </div>
          {/* フッター */}
          <NavFooter
            iconProps={iconProps}
            uiLibraryLink={uiLibraryLink}
            color={defaultTheme.palette.common.white}
            cssOverride={css`
              display: flex;
              flex-direction: column;
              padding-bottom: ${defaultTheme.spacing.footer.bottom};

              align-items: flex-start;
              @media screen and (min-width: ${defaultTheme.breakpoints.md}) {
                align-items: center;
              }
            `}
          />
        </div>
      </BaseInner>
    </Overlay>
  );
};
